import { useQuery } from '@tanstack/react-query';

import { fetchAssetsFromEndpoint } from '../services/svp/assets';
import { fetchCategories } from '../services/svp/categories';
import { fetchProfiles } from '../services/api/enrichments';
import { fetchAssetsFromBlend } from '../services/curate/blend';
import { getProvider } from '../services/svpProvider';
import { defaultStoryFilters } from '../components/PresetPicker/StoriesSearch/StoriesSearch';
import { isTruthy } from '../helpers/types';

import type {
  AssetDataSource,
  EnrichedTag,
  SvpAsset,
  SvpCategory,
} from '../types';
import type { CurateFetchParams } from '../services/curate/requestBlend';
import type { SvpFetchParams } from '../services/svp/shared/requestSVP';

export type EndpointItemsParams =
  | {
      endpoint: 'svp/assets' | 'svp/most-seen' | 'svp/search';
      params: SvpFetchParams;
      dataType: 'assets' | 'stories';
    }
  | {
      endpoint: 'svp/categories';
      params: Record<string, string>;
      dataType: 'categories';
    }
  | {
      endpoint: 'curate/blend';
      params: CurateFetchParams;
      dataType: 'assets';
    }
  | {
      endpoint?: never;
      params: { categoryId?: number };
      dataType: 'profiles';
    };

async function fetchEndpointItems(
  config: EndpointItemsParams,
  signal?: AbortSignal
): Promise<SvpAsset[] | SvpCategory[] | EnrichedTag[]> {
  const { dataType, endpoint, params } = config;

  if (dataType === 'assets') {
    if (endpoint === 'curate/blend') {
      return fetchAssetsFromBlend(
        getProvider(),
        params as CurateFetchParams,
        signal
      );
    }
    const assetsResult = await fetchAssetsFromEndpoint(
      endpoint as AssetDataSource,
      params as SvpFetchParams,
      signal
    );
    return assetsResult ?? [];
  }

  if (dataType === 'stories') {
    const defaultFilters = defaultStoryFilters[getProvider()] ?? {};
    const svpParams = params as SvpFetchParams;
    const storiesResult = await fetchAssetsFromEndpoint(
      endpoint as AssetDataSource,
      {
        ...svpParams,
        filter: {
          ...(svpParams?.filter as Record<string, string>),
          ...defaultFilters,
          tagsid: [
            defaultFilters?.tagsid,
            (svpParams?.filter as Record<string, string>)?.tagsid,
          ]
            .filter(isTruthy)
            .join(','),
        },
      },
      signal
    );
    return storiesResult ?? [];
  }

  if (dataType === 'categories') {
    return fetchCategories(params as Record<string, string>, signal);
  }

  if (dataType === 'profiles') {
    return fetchProfiles(
      (params as { categoryId?: number }).categoryId || undefined
    );
  }

  return [];
}

export const useEndpointItems = (config: EndpointItemsParams) => {
  return useQuery({
    queryKey: [
      'endpoint-items',
      config.dataType,
      config.endpoint,
      config.params,
    ],
    queryFn: ({ signal }) => fetchEndpointItems(config, signal),
  });
};
